import {
    CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR,
    CATEGORY_DESCRIPTION_MAX_LENGTH,
    CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR,
    CATEGORY_NAME_EMPTY_ERROR,
    CATEGORY_NAME_INVALID_CHAR_ERROR,
    CATEGORY_NAME_MAX_LENGTH,
    CATEGORY_NAME_MAX_LENGTH_ERROR,
    CATEGORY_ORDER_EMPTY_ERROR,
    CATEGORY_ORDER_GREATER_THAN_ZERO,
    CATEGORY_ORDER_INVALID_ERROR,
    CATEGORY_ORDER_MAX_LENGTH,
    CATEGORY_ORDER_MAX_LENGTH_ERROR,
    INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_ERROR,
    INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_MAX_LENGTH,
    INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_MAX_LENGTH_ERROR,
    INDICATOR_COUNTRY_VALUE_TYPE_INTEGER_ERROR,
    INDICATOR_COUNTRY_VALUE_TYPE_TEXT_MAX_LENGTH,
    INDICATOR_COUNTRY_VALUE_TYPE_TEXT_MAX_LENGTH_ERROR,
    INDICATOR_DESCRIPTION_INVALID_CHAR_ERROR,
    INDICATOR_DESCRIPTION_MAX_LENGTH,
    INDICATOR_DESCRIPTION_MAX_LENGTH_ERROR,
    INDICATOR_NAME_INVALID_CHAR_ERROR,
    INDICATOR_NAME_MAX_LENGTH,
    INDICATOR_NAME_MAX_LENGTH_ERROR,
    INDICATOR_NAME_REQUIRED,
    INDICATOR_ORDER_GREATER_THAN_ZERO,
    INDICATOR_ORDER_INVALID_ERROR,
    INDICATOR_ORDER_MAX_LENGTH,
    INDICATOR_ORDER_MAX_LENGTH_ERROR,
    INDICATOR_ORDER_REQUIRED,
    INDICATOR_REFERENCE_MAX_LENGTH,
    INDICATOR_REFERENCE_MAX_LENGTH_ERROR,
    INDICATOR_SOURCE_DATE_ERROR,
    INDICATOR_SOURCE_DATE_FORMAT_ERROR,
    INDICATOR_SOURCE_REQUIRED,
    INDICATOR_UPDATE_FREQUENCY_REQUIRED,
    INDICATOR_WEB_ADDRESS_REQUIRED,
    SUB_CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR,
    SUB_CATEGORY_DESCRIPTION_MAX_LENGTH,
    SUB_CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR,
    SUB_CATEGORY_NAME_EMPTY_ERROR,
    SUB_CATEGORY_NAME_INVALID_CHAR_ERROR,
    SUB_CATEGORY_NAME_MAX_LENGTH,
    SUB_CATEGORY_NAME_MAX_LENGTH_ERROR,
    SUB_CATEGORY_ORDER_EMPTY_ERROR,
    SUB_CATEGORY_ORDER_GREATER_THAN_ZERO,
    SUB_CATEGORY_ORDER_INVALID_ERROR,
    SUB_CATEGORY_ORDER_MAX_LENGTH,
    SUB_CATEGORY_ORDER_MAX_LENGTH_ERROR
} from 'common/config';
import { CategoryField, IndicatorDetailFields } from 'common/enums';
import { FieldError, IAsyncData } from 'common/models';
import { addValidationError } from './error';
import { Source } from 'common/models/data/entity.model';
import { IndicatorLocationTemp } from 'common/models/data/indicator.model';
import { EMPTY_GUID } from 'common/constants/user';
import { SubCategoryField } from 'common/enums/data.enum';

/**
 * Validates whether the field exceeds max length
 * @param value value to be validated
 * @param length max length
 * @returns True or false
 */
export const validateMaxLength = (value: string, length: number) => value.length > length;

/**
 * Validates if the string is valid UTF-8 chars
 * @param input Input string
 * @returns boolean value
 */
export const validateInputString = (input: string) => {
    let isValid = true;
    for (let i = 0; i < input.length; i++) {
        if (input.charCodeAt(i) > 127) {
            isValid = false;
            break;
        }
    }
    return isValid;
};
export const checkNumeric = (input: string) => {
    return /^[0-9]+$/.test(input);
};
/**
 * validates if the string is valid month year
 * @param input Input string
 * @returns boolean
 */
export const validateMonthYear = (input: string) => {
    const dateMonthYearRegEx =
        /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{4})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{4})$/;
    const monthYearRegex = /^(0?[1-9]|1[012])[\/]\d{4}$/;
    const yearRegex = /^\d{4}$/;

    if (monthYearRegex.test(input) || yearRegex.test(input) || dateMonthYearRegEx.test(input)) {
        return true;
    }
    return false;
};

/**
 * validates if the string is valid YY-MMM format
 * @param input Input string
 * @returns boolean
 */
export const validateYearMonthForCompanyFootprint = (input: string) => {
    const yearRegex = /^\d{2}-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/i;

    if (yearRegex.test(input)) {
        return true;
    }
    return false;
};

export const validateNumbers = (input: string) => {
    /**
     * RegEx for only positive integer numbers
     * const numberExp = /^[0-9]+$/;
     */
    const numberExp = /^[-+]?[0-9]+$/;
    return numberExp.test(input);
};

export const validateDecimalNumbers = (input: string) => {
    /**
     * RegEx for only positive decimal numbers
     * const decimalNumberExp = /^\d*\.?\d*$/;
     * RegEx for only positive two decimal number
     * const decimalNumberExp = /^[0-9]*(\.[0-9]{0,2})?$/;
     */
    const decimalNumberExp = /^[-+]?[0-9]*(\.[0-9]{0,2})?$/;
    return input.match(decimalNumberExp);
};

export const validateAndUpdateIndicatorModel = (
    field: string,
    value: any,
    errors: Array<FieldError>,
    sourceList: IAsyncData<Array<Source>>,
    indicatorModel: any
) => {
    if (field === IndicatorDetailFields.Name) {
        if (validateMaxLength(value, INDICATOR_NAME_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.Name,
                INDICATOR_NAME_MAX_LENGTH_ERROR
            );
        }
        if (value.trim().length === 0) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.Name,
                INDICATOR_NAME_REQUIRED
            );
        } else if (!validateInputString(value)) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.Name,
                INDICATOR_NAME_INVALID_CHAR_ERROR
            );
        }
    } else if (field === IndicatorDetailFields.Description) {
        if (validateMaxLength(value, INDICATOR_DESCRIPTION_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.Description,
                INDICATOR_DESCRIPTION_MAX_LENGTH_ERROR
            );
        }
        if (!validateInputString(value)) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.Description,
                INDICATOR_DESCRIPTION_INVALID_CHAR_ERROR
            );
        }
    } else if (field === IndicatorDetailFields.Reference) {
        if (validateMaxLength(value, INDICATOR_REFERENCE_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.Reference,
                INDICATOR_REFERENCE_MAX_LENGTH_ERROR
            );
        }
    } else if (field === IndicatorDetailFields.SourceDate) {
        if (value == '' || value == null) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.SourceDate,
                INDICATOR_SOURCE_DATE_FORMAT_ERROR
            );
        }
    } else if (field === IndicatorDetailFields.Order) {
        if (value !== '') {
            if (!validateNumbers(value)) {
                errors = addValidationError(
                    errors,
                    IndicatorDetailFields.Order,
                    INDICATOR_ORDER_INVALID_ERROR
                );
            } else {
                if (Number(value) === 0) {
                    errors = addValidationError(
                        errors,
                        IndicatorDetailFields.Order,
                        INDICATOR_ORDER_GREATER_THAN_ZERO
                    );
                } else if (Number(value) > INDICATOR_ORDER_MAX_LENGTH) {
                    errors = addValidationError(
                        errors,
                        IndicatorDetailFields.Order,
                        INDICATOR_ORDER_MAX_LENGTH_ERROR
                    );
                }
            }
        } else {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.Order,
                INDICATOR_ORDER_REQUIRED
            );
        }
    } else if (field === IndicatorDetailFields.FrequencyTypeId) {
        if (value == '' || value == null) {
            errors = addValidationError(
                errors,
                IndicatorDetailFields.FrequencyTypeId,
                INDICATOR_UPDATE_FREQUENCY_REQUIRED
            );
        }
    }
    if (field === IndicatorDetailFields.WebAddress) {
        return { data: { ...indicatorModel, [IndicatorDetailFields.SourceId]: value }, errors };
    } else {
        return { data: { ...indicatorModel, [field]: value }, errors };
    }
};

export const validateAndUpdateIndicatorValue = (
    countryName: string,
    value: any,
    errors: Array<FieldError>,
    indicatorCountryData: Array<IndicatorLocationTemp> | undefined,
    indicatorModel: any
) => {
    const newCountryData = indicatorCountryData?.map((item: IndicatorLocationTemp) => {
        if (item.locationName === countryName) {
            if (indicatorModel.valueType == 'Decimal') {
                if (!validateDecimalNumbers(value)) {
                    errors = addValidationError(
                        errors,
                        item.locationName,
                        INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_ERROR
                    );
                } else if (Number(value) > INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_MAX_LENGTH) {
                    errors = addValidationError(
                        errors,
                        item.locationName,
                        INDICATOR_COUNTRY_VALUE_TYPE_DECIMAL_MAX_LENGTH_ERROR
                    );
                }
                return {
                    ...item,
                    value
                };
            } else if (indicatorModel.valueType == 'Integer') {
                if (!validateNumbers(value)) {
                    errors = addValidationError(
                        errors,
                        item.locationName,
                        INDICATOR_COUNTRY_VALUE_TYPE_INTEGER_ERROR
                    );
                }
                return {
                    ...item,
                    value
                };
            } else {
                if (validateMaxLength(value, INDICATOR_COUNTRY_VALUE_TYPE_TEXT_MAX_LENGTH)) {
                    errors = addValidationError(
                        errors,
                        item.locationName,
                        INDICATOR_COUNTRY_VALUE_TYPE_TEXT_MAX_LENGTH_ERROR
                    );
                }
                return {
                    ...item,
                    value
                };
            }
        }
        return item;
    });

    return {
        data: newCountryData,
        errors
    };
};

export const validateIndicatorDetailsForm = (
    indicatorModel: any,
    data: any,
    errors: Array<FieldError>
) => {
    if (!indicatorModel[IndicatorDetailFields.Name]?.trim()) {
        errors = addValidationError(errors, IndicatorDetailFields.Name, INDICATOR_NAME_REQUIRED);
    }

    if (!indicatorModel[IndicatorDetailFields.Order]) {
        errors = addValidationError(errors, IndicatorDetailFields.Order, INDICATOR_ORDER_REQUIRED);
    }

    if (
        !indicatorModel[IndicatorDetailFields.SourceId] ||
        indicatorModel[IndicatorDetailFields.SourceId] === EMPTY_GUID
    ) {
        errors = addValidationError(
            errors,
            IndicatorDetailFields.WebAddress,
            INDICATOR_WEB_ADDRESS_REQUIRED
        );
    }

    if (
        indicatorModel[IndicatorDetailFields.SourceDate] == '' ||
        indicatorModel[IndicatorDetailFields.SourceDate] == null
    ) {
        errors = addValidationError(
            errors,
            IndicatorDetailFields.SourceDate,
            INDICATOR_SOURCE_DATE_ERROR
        );
    }

    if (!indicatorModel[IndicatorDetailFields.FrequencyTypeId]) {
        errors = addValidationError(
            errors,
            IndicatorDetailFields.FrequencyTypeId,
            INDICATOR_UPDATE_FREQUENCY_REQUIRED
        );
    }

    return errors;
};

export const validateCategoryDescription = (value: string, errors: Array<FieldError>) => {
    if (validateMaxLength(value, CATEGORY_DESCRIPTION_MAX_LENGTH)) {
        errors = addValidationError(
            errors,
            CategoryField.Description,
            CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR
        );
    }
    if (!validateInputString(value)) {
        errors = addValidationError(
            errors,
            CategoryField.Description,
            CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR
        );
    }
    return errors;
};

export const validateCategoryName = (value: string, errors: Array<FieldError>) => {
    if (value.trim().length === 0) {
        errors = addValidationError(errors, CategoryField.Name, CATEGORY_NAME_EMPTY_ERROR);
    }
    if (validateMaxLength(value, CATEGORY_NAME_MAX_LENGTH)) {
        errors = addValidationError(errors, CategoryField.Name, CATEGORY_NAME_MAX_LENGTH_ERROR);
    }
    if (!validateInputString(value)) {
        errors = addValidationError(errors, CategoryField.Name, CATEGORY_NAME_INVALID_CHAR_ERROR);
    }

    return errors;
};

export const validateCategorySortOrder = (value: string, errors: Array<FieldError>) => {
    if (value !== '') {
        if (!validateNumbers(value)) {
            errors = addValidationError(
                errors,
                CategoryField.SortOrder,
                CATEGORY_ORDER_INVALID_ERROR
            );
        } else {
            if (Number(value) === 0) {
                errors = addValidationError(
                    errors,
                    CategoryField.SortOrder,
                    CATEGORY_ORDER_GREATER_THAN_ZERO
                );
            } else if (Number(value) > CATEGORY_ORDER_MAX_LENGTH) {
                errors = addValidationError(
                    errors,
                    CategoryField.SortOrder,
                    CATEGORY_ORDER_MAX_LENGTH_ERROR
                );
            }
        }
    } else {
        errors = addValidationError(errors, CategoryField.SortOrder, CATEGORY_ORDER_EMPTY_ERROR);
    }
    return errors;
};

export const validateSubCategoryDescription = (value: string, errors: Array<FieldError>) => {
    if (validateMaxLength(value, SUB_CATEGORY_DESCRIPTION_MAX_LENGTH)) {
        errors = addValidationError(
            errors,
            SubCategoryField.Description,
            SUB_CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR
        );
    }
    if (!validateInputString(value)) {
        errors = addValidationError(
            errors,
            SubCategoryField.Description,
            SUB_CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR
        );
    }
    return errors;
};

export const validateSubCategoryName = (value: string, errors: Array<FieldError>) => {
    if (value.trim().length === 0) {
        errors = addValidationError(errors, SubCategoryField.Name, SUB_CATEGORY_NAME_EMPTY_ERROR);
    }
    if (validateMaxLength(value, SUB_CATEGORY_NAME_MAX_LENGTH)) {
        errors = addValidationError(
            errors,
            SubCategoryField.Name,
            SUB_CATEGORY_NAME_MAX_LENGTH_ERROR
        );
    }
    if (!validateInputString(value)) {
        errors = addValidationError(
            errors,
            SubCategoryField.Name,
            SUB_CATEGORY_NAME_INVALID_CHAR_ERROR
        );
    }
    return errors;
};

export const validateSubCategorySortOrder = (value: string, errors: Array<FieldError>) => {
    if (value !== '') {
        if (!validateNumbers(value)) {
            errors = addValidationError(
                errors,
                SubCategoryField.SortOrder,
                SUB_CATEGORY_ORDER_INVALID_ERROR
            );
        } else {
            if (Number(value) === 0) {
                errors = addValidationError(
                    errors,
                    SubCategoryField.SortOrder,
                    SUB_CATEGORY_ORDER_GREATER_THAN_ZERO
                );
            } else if (Number(value) > SUB_CATEGORY_ORDER_MAX_LENGTH) {
                errors = addValidationError(
                    errors,
                    SubCategoryField.SortOrder,
                    SUB_CATEGORY_ORDER_MAX_LENGTH_ERROR
                );
            }
        }
    } else {
        errors = addValidationError(
            errors,
            SubCategoryField.SortOrder,
            SUB_CATEGORY_ORDER_EMPTY_ERROR
        );
    }
    return errors;
};

export const validateCategoryForm = (
    name: string,
    sortOrder: string,
    errors: Array<FieldError>
) => {
    if (!name.trim()) {
        errors = addValidationError(errors, CategoryField.Name, CATEGORY_NAME_EMPTY_ERROR);
    }
    if (!sortOrder) {
        errors = addValidationError(errors, CategoryField.SortOrder, CATEGORY_ORDER_EMPTY_ERROR);
    }
    return errors;
};

export const validateSubCategoryForm = (
    name: string,
    sortOrder: string,
    errors: Array<FieldError>
) => {
    if (!name.trim()) {
        errors = addValidationError(errors, SubCategoryField.Name, SUB_CATEGORY_NAME_EMPTY_ERROR);
    }
    if (!sortOrder) {
        errors = addValidationError(
            errors,
            SubCategoryField.SortOrder,
            SUB_CATEGORY_ORDER_EMPTY_ERROR
        );
    }

    return errors;
};
